import React, {useRef} from "react";
import {Alert, Box, Button, FormControl, List, Typography} from "@mui/material";
import {useMutation} from "@tanstack/react-query";
import withDrawer, {SideDrawerProps} from "../../../../common/components/SideDrawerView";
import {useSnackbar} from "../../../../common/context/SnackbarContext";
import {RequestStatusSnackbarConfig} from "../../../../common/types/requests";
import {VocabularyActionDispatch} from "../../pages/Vocabulary";
import {useWordAddView} from "./hooks/useWordAddView";
import LemmaSearchBarView, {LemmaSearchBarViewHandle} from "./components/LemmaSearchBarView";
import TranslationsDropdownView from "./components/TranslationsDropdownView";
import PartOfSpeechResultView from "./components/PartOfSpeechResultView";
import SentenceResultView from "./components/SentenceResultView";

export interface WordAddViewProps extends SideDrawerProps {
    actionDispatch?: VocabularyActionDispatch
    snackbar?: RequestStatusSnackbarConfig
}

function WordAddView({actionDispatch, snackbar}: WordAddViewProps) {

    const {showSnackbar} = useSnackbar();

    const searchBarRef = useRef<LemmaSearchBarViewHandle>(null);

    const {
        lemma,
        setLemma,
        searchedLemma,
        lookupResults,
        selectedIndex,
        setSelectedIndex,
        search,
        isLookingUp,
        notFound,
        selectedResult,
        createWord,
        isCreatingWord,
        reset,
    } = useWordAddView();

    const {mutateAsync: addWord} = useMutation({
        mutationFn: ({lemma, index}: { lemma: string, index: number }) => createWord({lemma, index}),
        onError: async () => snackbar?.showRequestErrorSnackbar && showSnackbar("Failed to save word.", "error"),
    });

    const handleSearch = () => {
        if (isLookingUp) {
            return;
        }
        void search();
    };

    const handleReset = () => {
        reset();
        requestAnimationFrame(() => searchBarRef.current?.focus());
    };

    const handleAddWord = () => {
        if (searchedLemma === null || selectedIndex === null) {
            return;
        }

        snackbar?.showRequestSentSnackbar && showSnackbar("Saving word...", "info");

        const onAddedPromise = addWord({lemma: searchedLemma, index: selectedIndex});
        !!actionDispatch?.add && actionDispatch.add(onAddedPromise);
        onAddedPromise.catch(() => {});
        reset();
        requestAnimationFrame(() => searchBarRef.current?.focus());
    };

    return (
        <FormControl sx={{width: "100%"}}>
            <Typography variant="h5" align="center" fontWeight="bold">Add word</Typography>
            <Box sx={{mt: 2}}/>
            <LemmaSearchBarView ref={searchBarRef}
                                lemma={lemma}
                                onLemmaChange={setLemma}
                                onSearch={handleSearch}
                                onReset={handleReset}
                                isSearching={isLookingUp}
                                isLocked={isLookingUp || searchedLemma !== null}
            />
            {notFound && (
                <>
                    <Box sx={{mt: 1}}/>
                    <Alert severity="error" variant="outlined">
                        Word not found
                    </Alert>
                </>
            )}
            <Box sx={{mt: 2}}/>
            {!!lookupResults && lookupResults.length > 0 && selectedIndex !== null && selectedResult && (
                <>
                    <TranslationsDropdownView lookupResults={lookupResults}
                                              selectedIndex={selectedIndex}
                                              onSelectedIndexChange={setSelectedIndex}
                    />
                    <Box sx={{mt: 1}}/>
                    <List disablePadding>
                        <PartOfSpeechResultView partOfSpeech={selectedResult.partOfSpeech}/>
                        <SentenceResultView sentence={selectedResult.sentence}
                                            translation={selectedResult.sentenceTranslation}
                        />
                    </List>
                    <Box sx={{mt: 2}}/>
                    <Button variant="contained"
                            color="secondary"
                            fullWidth={true}
                            disabled={isCreatingWord}
                            onClick={handleAddWord}
                            sx={{borderRadius: 3}}
                    >
                        Add word
                    </Button>
                    <Box sx={{mt: 1}}/>
                </>
            )}
        </FormControl>
    );
}

export default withDrawer<WordAddViewProps>(WordAddView);
